import React, { useState, useEffect } from 'react';
import { AlertCircle, Upload, X, Bug, Plus, Settings, User, CreditCard, Shield, Zap } from 'lucide-react';
import { supportService, CreateTicketData, SupportCategory, TicketTemplate } from '../../lib/supportService';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Textarea } from '../common/Textarea';
import { Select } from '../common/Select';
import { Badge } from '../common/Badge';

interface SupportTicketFormProps {
  categories: SupportCategory[];
  onTicketCreated: (ticket: any) => void;
  onCancel: () => void;
}

export function SupportTicketForm({ categories, onTicketCreated, onCancel }: SupportTicketFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium' as const,
    tags: [] as string[]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TicketTemplate | null>(null);
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showFeatureRequest, setShowFeatureRequest] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<any>(null);

  useEffect(() => {
    loadTemplates();
    collectBrowserInfo();
  }, []);

  useEffect(() => {
    if (formData.category) {
      loadTemplateForCategory(formData.category);
    }
  }, [formData.category]);

  const loadTemplates = async () => {
    try {
      const templatesData = await supportService.getTicketTemplates();
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const loadTemplateForCategory = async (category: string) => {
    try {
      const template = await supportService.getTicketTemplate(category);
      setSelectedTemplate(template);
      if (template) {
        setFormData(prev => ({
          ...prev,
          title: template.title_template.replace('[Brief Description]', ''),
          description: template.description_template
        }));
      }
    } catch (err) {
      console.error('Failed to load template:', err);
    }
  };

  const collectBrowserInfo = async () => {
    try {
      const info = await supportService.collectBrowserInfo();
      setBrowserInfo(info);
    } catch (err) {
      console.error('Failed to collect browser info:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ticketData: CreateTicketData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority,
        tags: formData.tags,
        metadata: {
          browserInfo,
          templateUsed: selectedTemplate?.name,
          showBugReport,
          showFeatureRequest
        }
      };

      const newTicket = await supportService.createTicket(ticketData);

      // If this is a bug report, create additional bug report data
      if (showBugReport && formData.category === 'bug_report') {
        await supportService.createBugReport({
          ticket_id: newTicket.id,
          browser: browserInfo?.browser,
          browser_version: browserInfo?.browser_version,
          operating_system: browserInfo?.operating_system,
          os_version: browserInfo?.os_version,
          device_type: browserInfo?.device_type,
          screen_resolution: browserInfo?.screen_resolution,
          steps_to_reproduce: formData.description,
          expected_behavior: '',
          actual_behavior: '',
          frequency: 'sometimes',
          severity: formData.priority === 'critical' ? 'critical' : 
                   formData.priority === 'high' ? 'high' : 
                   formData.priority === 'medium' ? 'medium' : 'low'
        });
      }

      // If this is a feature request, create additional feature request data
      if (showFeatureRequest && formData.category === 'feature_request') {
        await supportService.createFeatureRequest({
          ticket_id: newTicket.id,
          feature_name: formData.title,
          feature_description: formData.description,
          estimated_impact: formData.priority === 'critical' ? 'critical' : 
                           formData.priority === 'high' ? 'high' : 
                           formData.priority === 'medium' ? 'medium' : 'low',
          implementation_complexity: 'medium'
        });
      }

      onTicketCreated(newTicket);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryName: string) => {
    switch (categoryName.toLowerCase()) {
      case 'bug_report':
        return <Bug size={16} />;
      case 'feature_request':
        return <Plus size={16} />;
      case 'account_issue':
        return <User size={16} />;
      case 'billing_issue':
        return <CreditCard size={16} />;
      case 'security_issue':
        return <Shield size={16} />;
      case 'performance_issue':
        return <Zap size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const categoryOptions = categories.map(category => ({
    value: category.name.toLowerCase().replace(/\s+/g, '_'),
    label: category.name,
    icon: getCategoryIcon(category.name.toLowerCase().replace(/\s+/g, '_'))
  }));

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'critical', label: 'Critical' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-400 mr-2" size={16} />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Category Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category *
        </label>
        <Select
          value={formData.category}
          onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
          options={categoryOptions}
          placeholder="Select a category"
        />
      </div>

      {/* Special Options for Bug Reports and Feature Requests */}
      {formData.category === 'bug_report' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Bug className="text-blue-500 mr-2" size={16} />
            <span className="text-blue-800 font-medium">Bug Report Options</span>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showBugReport}
              onChange={(e) => setShowBugReport(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-blue-700">
              Include detailed browser and system information
            </span>
          </label>
        </div>
      )}

      {formData.category === 'feature_request' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Plus className="text-green-500 mr-2" size={16} />
            <span className="text-green-800 font-medium">Feature Request Options</span>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showFeatureRequest}
              onChange={(e) => setShowFeatureRequest(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-green-700">
              Include detailed feature request information
            </span>
          </label>
        </div>
      )}

      {/* Priority Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Priority
        </label>
        <Select
          value={formData.priority}
          onChange={(value) => setFormData(prev => ({ ...prev, priority: value as any }))}
          options={priorityOptions}
          placeholder="Select priority"
        />
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title *
        </label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Brief description of your issue or request"
          maxLength={255}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Please provide detailed information about your issue or request..."
          rows={8}
        />
        <p className="text-xs text-gray-500 mt-1">
          Be as detailed as possible to help us assist you better.
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags (optional)
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.tags.map((tag, index) => (
            <Badge key={index} className="bg-blue-100 text-blue-800">
              {tag}
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  tags: prev.tags.filter((_, i) => i !== index)
                }))}
                className="ml-1 hover:text-blue-600"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex">
          <Input
            placeholder="Add a tag and press Enter"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const input = e.target as HTMLInputElement;
                const tag = input.value.trim();
                if (tag && !formData.tags.includes(tag)) {
                  setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, tag]
                  }));
                  input.value = '';
                }
              }
            }}
          />
        </div>
      </div>

      {/* Browser Information Display */}
      {browserInfo && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">System Information</h4>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div>Browser: {browserInfo.browser} {browserInfo.browser_version}</div>
            <div>OS: {browserInfo.operating_system} {browserInfo.os_version}</div>
            <div>Device: {browserInfo.device_type}</div>
            <div>Resolution: {browserInfo.screen_resolution}</div>
          </div>
        </div>
      )}

      {/* Template Information */}
      {selectedTemplate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Settings className="text-yellow-500 mr-2" size={16} />
            <span className="text-yellow-800 font-medium">Using Template: {selectedTemplate.name}</span>
          </div>
          <p className="text-sm text-yellow-700">
            This template will help you provide the most relevant information for your issue.
          </p>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          loading={loading}
        >
          Create Ticket
        </Button>
      </div>
    </form>
  );
} 